/**
 * Camera System Types and Constants
 * L4-ZOOM-001: Type definitions for battlefield zoom/pan controls
 */

/**
 * 2D point in either screen or world coordinates
 */
export interface Point {
  x: number;
  y: number;
}

/**
 * Current camera state - zoom level and pan offset
 */
export interface CameraState {
  /** Current zoom level (0.5 - 2.5) */
  zoom: number;
  /** Pan offset X in screen pixels */
  panX: number;
  /** Pan offset Y in screen pixels */
  panY: number;
}

/**
 * Camera configuration with limits and behavior settings
 */
export interface CameraConfig {
  /** Minimum zoom level (default: 0.5 for tactical overview) */
  minZoom: number;
  /** Maximum zoom level (default: 2.5 for detail view) */
  maxZoom: number;
  /** Zoom change per button click (default: 0.25) */
  zoomStep: number;
  /** Zoom change multiplier for wheel delta (default: 0.001) */
  wheelZoomFactor: number;
  /** Pan speed in pixels per frame at 1.0x zoom (default: 8) */
  panSpeed: number;
  /** Canvas width for pan constraints */
  canvasWidth: number;
  /** Canvas height for pan constraints */
  canvasHeight: number;
  /** Grid bounds for pan constraints (optional) */
  gridBounds?: {
    minX: number;
    minY: number;
    maxX: number;
    maxY: number;
  };
  /** Background world-space bounding box for pan clamping (optional).
   *  When set, panning is limited so the background always fills the viewport. */
  bgWorldBounds?: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };
}

/**
 * Default camera configuration
 * - Zoom range: 0.5x (tactical) to 2.5x (detail)
 * - Button zoom step: 0.25x per click
 * - Smooth wheel zoom with factor 0.001
 */
export const DEFAULT_CAMERA_CONFIG: CameraConfig = {
  minZoom: 0.8,  // Prevent zooming out too far (keeps arena visible)
  maxZoom: 2.5,
  zoomStep: 0.25,
  wheelZoomFactor: 0.001,
  panSpeed: 8,
  canvasWidth: 1200,
  canvasHeight: 700,
};

/**
 * Initial camera state - 1.5x zoom for closer view of creatures, no pan
 */
export const INITIAL_CAMERA_STATE: CameraState = {
  zoom: 1.5,
  panX: 0,
  panY: 0,
};

/**
 * Clamp a value between min and max
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}
